import { useState, useEffect, useRef, useCallback } from 'react';
import io from 'socket.io-client';
import axios from 'axios';
import { API_BASE_URL, SOCKET_URL } from '../config/api';

const API_BASE = API_BASE_URL;

/**
 * Hook personnalisé pour gérer toute la logique du chat dans les réunions
 * 
 * @param {Object} options - Options de configuration
 * @param {string} options.channelName - Nom du channel Agora
 * @param {string} options.displayName - Nom d'affichage de l'utilisateur
 * @param {number} options.clientUid - UID du client Agora
 * @param {Object} options.client - Client Agora (pour fallback Data Stream)
 * @param {Object} options.dataStream - Data Stream Agora (pour fallback)
 * @param {Function} options.onError - Callback pour les erreurs
 * @returns {Object} - État et fonctions du chat
 */
export const useMeetingChat = ({
  channelName,
  displayName,
  clientUid,
  client,
  dataStream,
  onError
}) => {
  // États
  const [socket, setSocket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isTyping, setIsTyping] = useState({}); // { uid: userName }
  const [chatOpen, setChatOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [socketConnected, setSocketConnected] = useState(false);

  // Refs
  const messagesEndRef = useRef(null);
  const chatInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Initialiser Socket.io quand le channel est disponible
  useEffect(() => {
    // Ne pas initialiser si channelName est vide (évite les reconnexions inutiles)
    if (!channelName || !channelName.trim() || !displayName) {
      // Nettoyer si on avait une connexion précédente
      if (socket) {
        console.log('[Chat] Nettoyage Socket.io (channelName vide)...');
        socket.disconnect();
        setSocket(null);
        setSocketConnected(false);
      }
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('[Chat] ⚠️ Pas de token, Socket.io non initialisé');
      return;
    }

    // Use centralized SOCKET_URL
    const socketUrl = SOCKET_URL || window.location.origin;
    console.log('[Chat] Tentative de connexion Socket.io à:', socketUrl);

    const socketInstance = io(socketUrl, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
      timeout: 20000,
      forceNew: false
    });

    socketInstance.on('connect', () => {
      console.log('✅ Socket.io connecté pour le chat');
      setSocketConnected(true);

      // Rejoindre la room de chat
      socketInstance.emit('join-chat-room', {
        channelName: channelName,
        uid: clientUid,
        userName: displayName || 'Utilisateur'
      });
      console.log('[Chat] ✅ join-chat-room émis');

      // Récupérer l'historique des messages
      fetchChatHistory(channelName);
    });

    socketInstance.on('connect_error', (error) => {
      // Ne pas logger les erreurs de connexion refusée comme des erreurs critiques
      // (le serveur peut ne pas être démarré ou être temporairement indisponible)
      if (error.message?.includes('ERR_CONNECTION_REFUSED') || 
          error.type === 'TransportError' || 
          error.message?.includes('websocket error')) {
        // Logger seulement une fois, pas à chaque tentative de reconnexion
        if (!socketInstance._connectionErrorLogged) {
          console.warn('[Chat] ⚠️ Backend server not available. Chat will be unavailable until server is started.');
          socketInstance._connectionErrorLogged = true;
        }
        setSocketConnected(false);
        return; // Ne pas appeler onError pour les erreurs de connexion refusée
      }
      
      console.error('[Chat] ❌ Erreur de connexion Socket.io:', error);
      setSocketConnected(false);
      
      let errorMsg = 'Erreur de connexion au chat. ';
      if (error.message.includes('timeout')) {
        errorMsg += `Le serveur ne répond pas sur ${socketUrl}. `;
        errorMsg += 'Vérifiez que le serveur backend est démarré.';
      } else if (error.message.includes('xhr poll error')) {
        errorMsg += 'Problème de connexion réseau.';
      } else {
        errorMsg += error.message || 'Erreur inconnue.';
      }
      
      if (onError) onError(errorMsg);
    });

    // Écouter les nouveaux messages
    socketInstance.on('new-message', (message) => {
      console.log('[Chat] 📨 Nouveau message reçu:', message);

      setMessages(prev => {
        // Éviter les doublons : vérifier par ID d'abord
        const existingById = prev.find(m => m.id === message.id);
        if (existingById) {
          console.log('[Chat] ⚠️ Message déjà présent (même ID), ignoré');
          return prev;
        }

        // Vérifier si c'est un doublon basé sur le contenu, l'expéditeur et le timestamp
        const isDuplicate = prev.some(m => {
          const sameContent = m.message === message.message;
          const sameSender = (m.senderUid === message.senderUid && m.senderUid !== undefined) ||
                            (m.senderUserId && message.senderUserId && 
                             m.senderUserId.toString() === message.senderUserId.toString());
          const sameTime = Math.abs((m.timestamp || 0) - (message.timestamp || 0)) < 2000;

          return sameContent && sameSender && sameTime;
        });

        if (isDuplicate) {
          console.log('[Chat] ⚠️ Doublon détecté, remplacement du message local par le message du serveur');
          return prev.map(m => {
            const isSameMessage = m.message === message.message &&
              ((m.senderUid === message.senderUid && m.senderUid !== undefined) ||
               (m.senderUserId && message.senderUserId && 
                m.senderUserId.toString() === message.senderUserId.toString())) &&
              Math.abs((m.timestamp || 0) - (message.timestamp || 0)) < 2000;

            if (isSameMessage && m.source === 'local') {
              return message;
            }
            return m;
          });
        }

        console.log('[Chat] ✅ Message ajouté à la liste');
        return [...prev, message];
      });

      // Notification si chat fermé
      if (!chatOpen) {
        setUnreadCount(prev => prev + 1);
      }

      // Scroll automatique vers le dernier message
      setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    });

    // Écouter les erreurs
    socketInstance.on('error', (error) => {
      console.error('[Chat] ❌ Erreur Socket.io:', error);
      if (error.message && onError) {
        onError(`Erreur chat: ${error.message}`);
      }
    });

    // Indicateur "en train d'écrire"
    socketInstance.on('user-typing', ({ uid, userName }) => {
      setIsTyping(prev => ({ ...prev, [uid]: userName }));

      // Auto-stop après 3 secondes
      setTimeout(() => {
        setIsTyping(prev => {
          const newTyping = { ...prev };
          delete newTyping[uid];
          return newTyping;
        });
      }, 3000);
    });

    socketInstance.on('user-stopped-typing', ({ uid }) => {
      setIsTyping(prev => {
        const newTyping = { ...prev };
        delete newTyping[uid];
        return newTyping;
      });
    });

    socketInstance.on('user-joined-chat', ({ uid, userName }) => {
      console.log(`[Chat] ${userName} a rejoint le chat`);
    });

    socketInstance.on('user-left-chat', ({ uid, userName }) => {
      console.log(`[Chat] ${userName} a quitté le chat`);
    });

    socketInstance.on('disconnect', (reason) => {
      console.log('❌ Socket.io déconnecté, raison:', reason);
      setSocketConnected(false);
      if (reason === 'io server disconnect') {
        socketInstance.connect();
      }
    });

    socketInstance.on('reconnect', (attemptNumber) => {
      console.log(`[Chat] ✅ Socket.io reconnecté après ${attemptNumber} tentatives`);
      setSocketConnected(true);
      if (onError) {
        onError('✅ Chat reconnecté avec succès !');
        setTimeout(() => onError(''), 3000);
      }

      // Rejoindre la room après reconnexion
      if (channelName) {
        socketInstance.emit('join-chat-room', {
          channelName: channelName,
          uid: clientUid,
          userName: displayName || 'Utilisateur'
        });
        console.log('[Chat] ✅ Re-join-chat-room émis après reconnexion');
      }
    });

    socketInstance.on('reconnect_attempt', (attemptNumber) => {
      console.log(`[Chat] 🔄 Tentative de reconnexion ${attemptNumber}...`);
    });

    socketInstance.on('reconnect_failed', () => {
      console.error('[Chat] ❌ Échec de la reconnexion après toutes les tentatives');
      setSocketConnected(false);
      if (onError) {
        onError('❌ Impossible de se reconnecter au chat. Veuillez rafraîchir la page.');
      }
    });

    setSocket(socketInstance);

    // Cleanup
    return () => {
      console.log('[Chat] Nettoyage Socket.io...');
      if (channelName) {
        socketInstance.emit('leave-chat-room', {
          channelName: channelName,
          uid: clientUid,
          userName: displayName
        });
      }
      socketInstance.disconnect();
      setSocket(null);
      setSocketConnected(false);
    };
  }, [channelName, displayName, clientUid]);

  // Récupérer l'historique des messages
  const fetchChatHistory = useCallback(async (channelName) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get(
        `${API_BASE}/agora/messages/${channelName}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.success && response.data.messages) {
        setMessages(response.data.messages);

        // Scroll vers le dernier message
        setTimeout(() => {
          if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      }
    } catch (error) {
      console.warn('Erreur récupération historique chat:', error);
    }
  }, []);

  // Envoyer un message
  const sendMessage = useCallback(() => {
    console.log('[Chat] sendMessage appelé');
    console.log('[Chat] socket:', socket);
    console.log('[Chat] socket.connected:', socket?.connected);
    console.log('[Chat] newMessage:', newMessage);
    console.log('[Chat] channelName:', channelName);

    if (!socket) {
      console.error('[Chat] ❌ Socket non disponible');
      if (onError) {
        onError('Erreur: Connexion chat non disponible. Veuillez rafraîchir la page.');
      }
      return;
    }

    if (!socket.connected) {
      console.error('[Chat] ❌ Socket non connecté');
      if (onError) {
        onError('Erreur: Connexion chat perdue. Reconnexion en cours...');
      }
      return;
    }

    if (!newMessage.trim()) {
      console.warn('[Chat] ⚠️ Message vide');
      return;
    }

    if (!channelName) {
      console.error('[Chat] ❌ channelName manquant');
      if (onError) {
        onError('Erreur: Configuration de réunion manquante.');
      }
      return;
    }

    const messageText = newMessage.trim();
    setNewMessage('');

    console.log('[Chat] ✅ Envoi du message:', messageText);
    console.log('[Chat] Channel:', channelName);

    // Envoyer via WebSocket
    try {
      socket.emit('send-message', {
        channelName: channelName,
        message: messageText,
        messageType: 'text'
      });
      console.log('[Chat] ✅ Message émis via WebSocket');

      // Ajouter le message localement immédiatement pour un feedback instantané
      const localMessage = {
        id: `local-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
        channelName: channelName,
        senderUid: clientUid,
        senderName: displayName || 'Vous',
        senderUserId: JSON.parse(localStorage.getItem('user') || '{}')._id,
        message: messageText,
        messageType: 'text',
        timestamp: Date.now(),
        source: 'local'
      };

      setMessages(prev => {
        const existingLocal = prev.find(m =>
          m.source === 'local' &&
          m.message === localMessage.message &&
          m.senderUid === localMessage.senderUid &&
          Math.abs((m.timestamp || 0) - (localMessage.timestamp || 0)) < 1000
        );

        if (!existingLocal) {
          return [...prev, localMessage];
        }
        return prev;
      });

      // Scroll vers le dernier message
      setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } catch (error) {
      console.error('[Chat] ❌ Erreur lors de l\'envoi:', error);
      if (onError) {
        onError('Erreur lors de l\'envoi du message. Veuillez réessayer.');
      }
    }

    // Optionnel : aussi envoyer via Agora Data Stream pour fallback
    if (dataStream && client) {
      try {
        const messageData = JSON.stringify({
          type: 'chat-message',
          message: messageText,
          senderName: displayName,
          timestamp: Date.now()
        });
        client.sendStreamMessage(dataStream, new TextEncoder().encode(messageData));
        console.log('[Chat] ✅ Message aussi envoyé via Agora Data Stream (fallback)');
      } catch (e) {
        console.warn('[Chat] ⚠️ Erreur envoi via Agora:', e);
      }
    }

    // Arrêter l'indicateur "en train d'écrire"
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    try {
      socket.emit('stop-typing', { channelName: channelName });
    } catch (e) {
      console.warn('[Chat] ⚠️ Erreur stop-typing:', e);
    }
  }, [socket, newMessage, channelName, clientUid, displayName, dataStream, client, onError]);

  // Gérer "en train d'écrire"
  const handleTyping = useCallback(() => {
    if (!socket || !channelName) return;

    socket.emit('typing', { channelName: channelName });

    // Clear timeout précédent
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Arrêter après 3 secondes d'inactivité
    typingTimeoutRef.current = setTimeout(() => {
      socket.emit('stop-typing', { channelName: channelName });
    }, 3000);
  }, [socket, channelName]);

  // Formater le timestamp
  const formatTime = useCallback((timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;

    if (diff < 60000) {
      return 'À l\'instant';
    } else if (diff < 3600000) {
      const minutes = Math.floor(diff / 60000);
      return `Il y a ${minutes} min`;
    } else if (date.toDateString() === now.toDateString()) {
      return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  }, []);

  // Scroll automatique vers le dernier message
  useEffect(() => {
    if (chatOpen && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, chatOpen]);

  // Réinitialiser le compteur de messages non lus quand le chat s'ouvre
  useEffect(() => {
    if (chatOpen) {
      setUnreadCount(0);
    }
  }, [chatOpen]);

  // Fonction pour ajouter un message manuellement (pour Agora Data Stream fallback)
  const addMessage = useCallback((message) => {
    setMessages(prev => {
      // Éviter les doublons
      if (!prev.find(m => m.id === message.id)) {
        return [...prev, message];
      }
      return prev;
    });

    // Notification si chat fermé
    if (!chatOpen) {
      setUnreadCount(prev => prev + 1);
    }

    // Scroll automatique vers le dernier message
    setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  }, [chatOpen]);

  return {
    // États
    socket,
    messages,
    newMessage,
    setNewMessage,
    isTyping,
    chatOpen,
    setChatOpen,
    unreadCount,
    socketConnected,
    // Refs
    messagesEndRef,
    chatInputRef,
    // Fonctions
    sendMessage,
    handleTyping,
    formatTime,
    addMessage
  };
};

